import React, { useMemo, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import { formatCurrencyINR } from '../../../utils/currency';
import { useUserData } from '../../../hooks/useUserData';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { truncateProjectionData, getChartTruncationConfig } from '../../../utils/chartUtils';
import ChartUpgradePrompt from '../../../components/charts/ChartUpgradePrompt';

const PerformanceTracker = ({ assets }) => {
  const { userData } = useUserData();
  const { canProjectBeyond5Years } = useSubscription();

  // Determine top 4 assets by projected wealth growth over full horizon
  const top4Assets = useMemo(() => {
    const startYear = Number(userData?.setup?.startYear) || new Date().getFullYear();
    const horizon = Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20));
    const fallbackReturnByType = {
      'equity': 0.10,
      'mutual funds': 0.08,
      'real estate': 0.06,
      'ppf': 0.07,
      'fixed deposits': 0.05,
      'gold': 0.04,
      'bonds': 0.06,
      'insurance': 0.03,
      'other': 0.06
    };
    const ranked = (assets || []).map(a => {
      const base = Math.max(0, Number(a.currentValue) || 0);
      const typeKey = String(a.type || 'other').toLowerCase();
      const r = Number(a.expectedReturn) > 0 ? (Number(a.expectedReturn) / 100) : (fallbackReturnByType[typeKey] ?? 0.06);
      const future = base * Math.pow(1 + r, horizon);
      return { id: a.id, name: a.name || a.type || 'Asset', base, r, growth: Math.max(0, future - base) };
    }).sort((a, b) => b.growth - a.growth);
    return ranked.slice(0, 4);
  }, [assets, userData]);

  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const [projectionData, setProjectionData] = useState([]);
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/asset/portfolio/performance${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load performance');
        const json = await res.json();
        if (json?.success && json.data) {
          // Merge topSeries into line keys like before
          const series = json.data.series || [];
          const byTypeSeries = json.data.byTypeSeries || [];
          const merged = series.map((row) => {
            const out = { year: row.year, portfolioValue: row.portfolioValue };
            byTypeSeries.forEach((ts) => {
              const point = ts.series.find((p) => p.year === row.year);
              if (point) out[`type_${ts.name}`] = point.value;
            });
            return out;
          });
          setProjectionData(merged);
        } else {
          setProjectionData([]);
        }
      } catch (e) {
        console.error(e);
        setProjectionData([]);
      }
    };
    fetchPerformance();
  }, [assets, getDemoQueryParams, getAuthHeaders]);
  
  // Calculate performance metrics
  // Summary from backend (fallback kept simple if needed)
  const [summary, setSummary] = useState({ totalReturn: 0, outperformance: 0, volatility: 0 });
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/asset/portfolio/performance${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load performance summary');
        const json = await res.json();
        if (json?.success && json.data?.summary) setSummary(json.data.summary);
      } catch (e) {
        console.error(e);
        setSummary({ totalReturn: 0, outperformance: 0, volatility: 0 });
      }
    };
    fetchSummary();
  }, [assets, getDemoQueryParams, getAuthHeaders]);
  
  // Asset performance breakdown
  const assetPerformance = assets.map(asset => {
    const purchaseValue = asset.purchaseValue || asset.currentValue;
    const returnPercent = ((asset.currentValue - purchaseValue) / purchaseValue) * 100;
    
    return {
      name: asset.name.length > 15 ? asset.name.substring(0, 15) + '...' : asset.name,
      return: returnPercent,
      value: asset.currentValue,
      type: asset.type
    };
  }).sort((a, b) => b.return - a.return);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrencyINR(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PerformanceTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            Return: {data.return.toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground">
            Value: {formatCurrencyINR(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={16} className="text-accent" />
            </div>
            <h4 className="font-medium text-foreground">Total Return</h4>
          </div>
          <p className="text-2xl font-bold text-foreground">{Number(summary.totalReturn || 0).toFixed(1)}%</p>
          <p className="text-sm text-muted-foreground">Year to date</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Target" size={16} className="text-primary" />
            </div>
            <h4 className="font-medium text-foreground">vs Sensex</h4>
          </div>
          <p className={`text-2xl font-bold ${Number(summary.outperformance || 0) >= 0 ? 'text-accent' : 'text-destructive'}`}>
            {Number(summary.outperformance || 0) >= 0 ? '+' : ''}{Number(summary.outperformance || 0).toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground">Relative to Sensex (YTD)</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={16} className="text-warning" />
            </div>
            <h4 className="font-medium text-foreground">Volatility</h4>
          </div>
          <p className="text-2xl font-bold text-foreground">{Number(summary.volatility || 0).toFixed(1)}%</p>
          <p className="text-sm text-muted-foreground">Standard deviation</p>
        </div>
      </div>

      {/* Portfolio Performance Chart */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="LineChart" size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Portfolio Performance</h3>
              <p className="text-sm text-muted-foreground">Projected over planning horizon (yearly)</p>
            </div>
          </div>
          
          {/* Legend removed; use tooltip for clarity */}
        </div>

        <div className="relative" style={{ height: '320px' }}>
          {(() => {
            const currentYear = new Date().getFullYear();
            const startYear = Number(userData?.setup?.startYear) || currentYear;
            const fullTotalYears = projectionData.length;
            const truncationConfig = getChartTruncationConfig('PortfolioPerformance', canProjectBeyond5Years, isDemoModeActive);
            const shouldTruncate = truncationConfig.shouldTruncate;
            const freeYearsLimit = 5;
            const displayYearsLimit = freeYearsLimit * 2; // 10 years for free users only
            const rawData = shouldTruncate ? projectionData.slice(0, displayYearsLimit + 1) : projectionData;
            const displayData = rawData.map((row, idx) => {
              if (shouldTruncate && idx > freeYearsLimit) {
                const lastKnownValue = projectionData[freeYearsLimit]?.portfolioValue || 0;
                const trendMultiplier = 1 + (0.08 * (idx - freeYearsLimit));
                return {
                  ...row,
                  portfolioValue: lastKnownValue * trendMultiplier,
                  isBlurred: true
                };
              }
              return { ...row, isBlurred: false };
            });
            
            // Show year labels every 2 years
            const xAxisTicks = [];
            displayData.forEach((d, i) => {
              if (i % 2 === 0) { // Every 2nd year
                xAxisTicks.push(d.year);
              }
            });

            return (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={displayData}>
                    <defs>
                      {/* Gradient for the blurred section - transitions at 50% (year 5 of 10) */}
                      <linearGradient id="blurGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="1" />
                        <stop offset="48%" stopColor="var(--color-primary)" stopOpacity="1" />
                        <stop offset="55%" stopColor="var(--color-primary)" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.15" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="year" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      ticks={xAxisTicks}
                      height={50}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      tickFormatter={(value) => formatCurrencyINR(value, { compact: true })}
                    />
                    <Tooltip 
                      content={(props) => {
                        if (props.active && props.payload && props.payload.length) {
                          const isBlurred = props.payload[0]?.payload?.isBlurred;
                          if (isBlurred) {
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                                <p className="font-medium text-muted-foreground flex items-center gap-2">
                                  <Icon name="Lock" size={14} />
                                  Locked Content
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Upgrade to view full projections
                                </p>
                              </div>
                            );
                          }
                          return <CustomTooltip {...props} />;
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="portfolioValue" 
                      stroke={shouldTruncate ? 'url(#blurGradient)' : 'var(--color-primary)'}
                      strokeWidth={2}
                      name="Portfolio"
                      dot={false}
                      strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                    />
                    {Object.keys(projectionData[0] || {}).filter(k => k.startsWith('type_')).map((key, idx) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={`hsl(${(idx * 80) % 360}, 70%, 50%)`}
                        strokeWidth={2}
                        name={key.replace('type_','')}
                        dot={false}
                        strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                        strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                
                {/* Blurred overlay with gradient and CTA */}
                {shouldTruncate && fullTotalYears > freeYearsLimit && (
                  <div 
                    className="absolute top-0 right-0 pointer-events-none"
                    style={{ 
                      left: '48%', // Start blur at 48% (just before year 5 marker)
                      bottom: '50px', // Account for X-axis labels
                      background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                      backdropFilter: 'blur(5px)',
                    }}
                  >
                    {/* Dark mode gradient overlay */}
                    <div 
                      className="absolute inset-0 dark:block hidden"
                      style={{ 
                        background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                      }}
                    />
                    
                    {/* Upgrade CTA Card */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                        <div className="flex items-center justify-center mb-3">
                          <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                            <Icon name="Lock" size={22} className="text-white" />
                          </div>
                        </div>
                        <h4 className="font-bold text-center mb-1 text-sm">
                          {(userData?.setup?.projectionHorizon || 20) - freeYearsLimit}+ Years Locked
                        </h4>
                        <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                          This is where compounding matters most.
                        </p>
                        <a
                          href="/upgrade"
                          className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                        >
                          See My Full Picture
                        </a>
                      </div>
                    </div>
                    
                    {/* Vertical separator line at truncation point */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                      style={{ 
                        boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                      }}
                    />
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Individual Asset Performance - hidden as requested
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="BarChart3" size={16} className="text-accent" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Asset Performance</h3>
            <p className="text-sm text-muted-foreground">Individual asset returns</p>
          </div>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={assetPerformance} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                type="number" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis 
                type="category"
                dataKey="name" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                width={120}
              />
              <Tooltip content={<PerformanceTooltip />} />
              <Bar 
                dataKey="return" 
                fill="var(--color-primary)"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      */}
    </div>
  );
};

export default PerformanceTracker;